import { SmartTableColumn } from '../types';

export interface TableState {
  visibleColumns: string[];
  filters: Record<string, any>;
  sorters: Array<{
    field: string;
    order: 'asc' | 'desc';
  }>;
  search: {
    value: string;
    fields: string[];
  };
}

export interface TableStateManager {
  getInitialState: (resource: string, columns: SmartTableColumn[]) => TableState;
  getState: (resource: string) => TableState | null;
  setState: (resource: string, state: Partial<TableState>, columns: SmartTableColumn[]) => void;
  clearState: (resource: string) => void;
}

class TableStateManagerImpl implements TableStateManager {
  private getDefaultState(columns: SmartTableColumn[]): TableState {
    return {
      visibleColumns: columns ? columns.map(col => col.key) : [],
      filters: {},
      sorters: [],
      search: {
        value: '',
        fields: [],
      },
    };
  }

  getInitialState(resource: string, columns: SmartTableColumn[]): TableState {
    const storedState = this.getState(resource);
    return storedState || this.getDefaultState(columns);
  }

  getState(resource: string): TableState | null {
    try {
      const state = localStorage.getItem(`table_state_${resource}`);
      return state ? JSON.parse(state) : null;
    } catch {
      return null;
    }
  }

  setState(resource: string, state: Partial<TableState>, columns: SmartTableColumn[]): void {
    try {
      const currentState = this.getState(resource) || this.getDefaultState(columns);
      const newState = { ...currentState, ...state };
      localStorage.setItem(`table_state_${resource}`, JSON.stringify(newState));
    } catch {
      // Handle storage errors silently
    }
  }

  clearState(resource: string): void {
    try {
      localStorage.removeItem(`table_state_${resource}`);
    } catch {
      // Handle storage errors silently
    }
  }
}

// Export singleton instance
export const tableStateManager = new TableStateManagerImpl();
